import { useCallback, useEffect, useMemo } from "react";
import { BranchCode, GetUnemploymentRiskResponse, GetUpiRiskResponse, GetRgiRiskResponse, GetLegalProtectionRiskResponse } from "api/risk";
import { PersonDto } from "api/contract";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { NsKey } from "enums/NsKey";
import { useActorRegistration } from "./useActorRegistration";
import { Sizes } from "components/forms/NumberSelector";
import { ActorFormValues, useSubmitActorForm } from "features/new-actor/hooks/optim/useSubmitActorForm";
import { usePanel } from "hooks/usePanel";
import { useSubscriptionProcessState } from "features/subscription-process/contexts/selectors/selectors";

export interface ActorRegistrationPanelOptions {
  /** Person to edit instead of the one already registered on the risk (e.g. picked in a search). */
  initialPerson?: PersonDto;
  /**
   * Called once the save succeeded. When given, the panel is no longer closed right after
   * `submitForm()`: closing it is up to this callback, which only runs on success.
   */
  postSubmit?: (values: Partial<ActorFormValues>) => void;
  /**
   * Risk to register the person on. A panel is rendered outside the matched route, so
   * `useParams()` finds no `riskId` there: the opener, which is inside the route, passes it.
   */
  riskId?: string;
  /** Legal entity: save the accounting data alone once the person is saved (see useSubmitActorForm). */
  withFinancials?: boolean;
}

export const useActorRegistrationPanel = (branchCode: BranchCode, options: ActorRegistrationPanelOptions = {}) => {
  const { initialPerson, postSubmit, riskId: riskIdOverride, withFinancials } = options;
  const { riskId: routeRiskId, formikRef, isFormValid, selectedPerson, setSelectedPerson, ...rest } = useActorRegistration(branchCode)
  const riskId = riskIdOverride ?? routeRiskId
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);
  const { setPanelActions, closePanel } = usePanel()

  const { riskData } = useSubscriptionProcessState()

  // Extract person from riskData based on type
  const person = useMemo(() => {
    if (initialPerson) return initialPerson
    if (!riskData) return undefined
    return (riskData as GetUnemploymentRiskResponse | GetUpiRiskResponse | GetRgiRiskResponse | GetLegalProtectionRiskResponse).person
  }, [initialPerson, riskData])

  useEffect(() => {
    setSelectedPerson(person)
  }, [person, setSelectedPerson])

  const handleSubmitClick = useCallback(() => {
    const formik = formikRef.current;
    if (!formik) return;
    formikRef.current?.submitForm()
    // Without a postSubmit hook there is no success signal to wait for: close as before.
    if (!postSubmit) {
      closePanel()
    }
  }, [closePanel, formikRef, postSubmit])

  useEffect(() => {
    setPanelActions({
      closeLabel: "COMMON.CANCEL",
      submitLabel: "COMMON.VALIDATE",
      submitDisabled: !isFormValid,
      onSubmit: handleSubmitClick
    })
  }, [formikRef, isFormValid, handleSubmitClick, setPanelActions])

  const stepPageConfig = useMemo(() => ({
    nextConfig: {
      label: t('COMMON.NEXT_STEP'),
      onClick: handleSubmitClick,
      disabled: !isFormValid,
    },
    prevConfig: {
      label: t('COMMON.CANCEL'),
      onClick: () => setSelectedPerson(undefined),
    },
    containerSize: Sizes["5xl"],
  }), [t, setSelectedPerson, isFormValid, handleSubmitClick]);

  const { handleSubmit, editConfig } = useSubmitActorForm({ person: selectedPerson, postSubmit, riskId, formikRef, withFinancials });

  return {
    ...rest,
    formikRef,
    handleSubmit,
    editConfig,
    handleSubmitClick,
    stepPageConfig,
    selectedPerson
  };
};
